import axios, { AxiosError } from "axios";
import { SettingsContext } from '@/utilities/settings-context';
import { promotionsWithCredits, PromotionType, sortPromotionType } from "@/states/promotion-state";

type EnableCreditRequest = {
  companyId: number;
  description: string;
}

type SetCreditReservationMaxPeriodRequest = {
  companyId: number;
  maxPeriodDays: number;
}

type CreditRequest = {
  companyId: number;
  amount: CreditAmount[];
  description: string;
}

export type CreditInformation = {
  enabled: boolean;
  maxPeriodDays: number;
  updatedAt: Date;
  amounts: CreditAmount[];
}

type CreditInformationResponse = {
  enabled: boolean;
  maxPeriodDays: number;
  updatedAt: string;
  amounts: CreditAmount[];
}

export type CreditAmount = { type: PromotionType; amount: number };

export type CreditTransactionType = 'use' | 'buy';

export type CreditTransactionItem = {
    type: CreditTransactionType;
    title: string;
    description: string;
    issuer: string | null;
    createdAt: string;
    amount: CreditAmount[];
};

const handleError = (error: Error | AxiosError<unknown> | unknown, message: string) => {
  if (axios.isAxiosError(error) && error.response) {
    throw new Error(`${message}: ${error.response.data}`);
  } else if(error instanceof Error) {
    throw new Error(`${message}: ${error.message}`);
  }
}

export const addBoostingCredits = async (request: CreditRequest) => {
  try {
    await axios.post(`${SettingsContext.getApiUrl('addBoostingCredits')}/${request.companyId}`, {
      amount: request.amount,
      description: request.description,
    });
  } catch (error) {
    handleError(error, 'Failed to add Boosting credits');
  }
}

export const getCreditInformation = async (companyId: number): Promise<CreditInformation> => {
  try {
    const response = await axios.get<CreditInformationResponse>(`${SettingsContext.getApiUrl('getCreditInformation')}/${companyId}`);

    return {
      amounts: response.data.amounts.sort((a, b) => sortPromotionType(a.type, b.type)),
      maxPeriodDays: response.data.maxPeriodDays,
      updatedAt: new Date(response.data.updatedAt),
      enabled: response.data.enabled,
    };
  } catch (error) {
    handleError(error, 'Failed to get credit information');
  }

  return {
    amounts: promotionsWithCredits.map(x => ({type: x, amount: 0})),
    maxPeriodDays: 180,
    updatedAt: new Date(0),
    enabled: false,
  };
}

export const getCreditTransactions = async (id: number, page: number, pageSize = 1000000) => {
  try {
    const transactions = await axios.get<CreditTransactionItem[]>(SettingsContext.getApiUrl('creditTransactions') + `/${id}`, {params: {page, pageSize}});

    return transactions.data;
  } catch (error) {
    handleError(error, 'Failed to get credit information');
  }

  return [];
};


export const enableCredits = async (request: EnableCreditRequest) => {
  try {
    await axios.post(`${SettingsContext.getApiUrl('enableCredits')}/${request.companyId}`, {
      description: request.description,
    });
  } catch (error) {
    handleError(error, 'Failed to enable credits');
  }
}

export const setCreditReservationMaxPeriod = async (request: SetCreditReservationMaxPeriodRequest) => {
  try {
    await axios.post(`${SettingsContext.getApiUrl('setCreditReservationMaxPeriod')}/${request.companyId}`, {
      maxPeriodDays: request.maxPeriodDays,
    });
  } catch (error) {
    handleError(error, 'Failed to set reservation max period');
  }
}

export const disableCredits = async (companyId: number) => {
  try {
    await axios.post(`${SettingsContext.getApiUrl('disableCredits')}/${companyId}`);
  } catch (error) {
    handleError(error, 'Failed to disable credits');
  }
}
